// frontend/src/pages/Solutions.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    MagnifyingGlassIcon,
    StarIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const Solutions = () => {
    const [loading, setLoading] = useState(true);
    const [solutions, setSolutions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filters, setFilters] = useState({
        search: '',
        category: '',
        industry: ''
    });
    const [selectedSolution, setSelectedSolution] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        loadData();
    }, [filters]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [solutionsRes, categoriesRes] = await Promise.all([
                axios.get('/solutions', { params: filters }),
                axios.get('/solutions/categories')
            ]);
            setSolutions(solutionsRes.data.data);
            setCategories(categoriesRes.data.data || []);
        } catch (error) {
            toast.error('Failed to load solutions');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters({ ...filters, [key]: value });
    };

    const handleViewDetails = async (solutionId) => {
        try {
            const response = await axios.get(`/solutions/${solutionId}`);
            setSelectedSolution(response.data.data);
            setShowDetails(true);
        } catch (error) {
            toast.error('Failed to load solution details');
        }
    };

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Solutions Library</h1>
                    <p className="text-gray-600 mt-1">
                        Discover AI solutions for business challenges
                    </p>
                </div>
                <Link 
                    to="/solutions/matches" 
                    className="px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                >
                    My Matched Solutions
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder="Search solutions..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm"
                            />
                        </div>
                    </div>
                    <select
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                    >
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                    <select
                        value={filters.industry}
                        onChange={(e) => handleFilterChange('industry', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                    >
                        <option value="">All Industries</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Retail">Retail</option>
                        <option value="Finance">Finance</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Technology">Technology</option>
                    </select>
                </div>
            </div>

            {/* Solutions Grid */}
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="text-gray-600">Loading solutions...</div>
                </div>
            ) : solutions.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
                    No solutions found matching your criteria
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {solutions.map((solution) => (
                        <SolutionCard 
                            key={solution.id} 
                            solution={solution}
                            onViewDetails={() => handleViewDetails(solution.id)}
                        />
                    ))}
                </div>
            )}

            {/* Details Modal */}
            {showDetails && selectedSolution && (
                <SolutionDetailModal 
                    solution={selectedSolution}
                    onClose={() => setShowDetails(false)}
                />
            )}
        </div>
    );
};

const SolutionCard = ({ solution, onViewDetails }) => {
    let parsedIndustries = [];
    if (solution.industries) {
        parsedIndustries = typeof solution.industries === 'string' 
            ? JSON.parse(solution.industries) 
            : solution.industries;
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col justify-between">
            <div>
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{solution.name}</h3>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {solution.description}
                        </p>
                    </div>
                    {solution.featured && (
                        <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full ml-2 whitespace-nowrap font-medium">
                            Featured
                        </span>
                    )}
                </div>

                <div className="mt-3 space-y-2">
                    {solution.category && (
                        <span className="inline-block text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-700 font-medium">
                            {solution.category}
                        </span>
                    )}
                    {Number(solution.rating) > 0 && (
                        <div className="flex items-center space-x-1">
                            <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span className="text-sm font-medium">{solution.rating}</span>
                            <span className="text-xs text-gray-500">({solution.review_count || 0})</span>
                        </div>
                    )}
                    {parsedIndustries.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {parsedIndustries.slice(0, 3).map(industry => (
                                <span key={industry} className="text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                                    {industry}
                                </span>
                            ))}
                            {parsedIndustries.length > 3 && (
                                <span className="text-xs text-gray-400">+{parsedIndustries.length - 3}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <button
                onClick={onViewDetails}
                className="mt-4 w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium text-gray-700"
            >
                View Details
            </button>
        </div>
    );
};

const SolutionDetailModal = ({ solution, onClose }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [assessments, setAssessments] = useState([]);
    const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
    const [loadingAssessments, setLoadingAssessments] = useState(true);
    const [matching, setMatching] = useState(false);

    useEffect(() => {
        const loadAssessments = async () => {
            try {
                setLoadingAssessments(true);
                const response = await axios.get('/solutions/match-assessments');
                const items = response.data.data || [];
                setAssessments(items);
                if (items.length > 0) {
                    setSelectedAssessmentId(String(items[0].id));
                }
            } catch (error) {
                toast.error('Failed to load business assessments');
            } finally {
                setLoadingAssessments(false);
            }
        };
        loadAssessments();
    }, []);

    const handleMatchSolution = async () => {
        if (!selectedAssessmentId) {
            toast.error('Select a business assessment to match this solution');
            return;
        }

        setMatching(true);
        try {
            await axios.post('/solutions/match', {
                solution_id: solution.id,
                business_assessment_id: Number(selectedAssessmentId)
            });
            toast.success('Solution matched successfully!');
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to match solution');
        } finally {
            setMatching(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {solution.name}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6">
                    {/* Tabs */}
                    <div className="flex space-x-4 border-b border-gray-200 mb-4">
                        {['overview', 'training', 'reviews', 'demos'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                                    activeTab === tab
                                        ? 'border-black text-gray-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="space-y-4">
                        {activeTab === 'overview' && (
                            <div>
                                <p className="text-gray-700">{solution.description}</p>
                                
                                <div className="mt-4 grid grid-cols-2 gap-4">
                                    {solution.category && (
                                        <div>
                                            <p className="text-sm text-gray-500">Category</p>
                                            <p className="font-medium">{solution.category}</p>
                                        </div>
                                    )}
                                    {solution.implementation_time_weeks && (
                                        <div>
                                            <p className="text-sm text-gray-500">Implementation Time</p>
                                            <p className="font-medium">{solution.implementation_time_weeks} weeks</p>
                                        </div>
                                    )}
                                    {solution.cost_range_min && (
                                        <div>
                                            <p className="text-sm text-gray-500">Cost Range</p>
                                            <p className="font-medium">
                                                ${solution.cost_range_min} - ${solution.cost_range_max}
                                            </p>
                                        </div>
                                    )}
                                    {Number(solution.rating) > 0 && (
                                        <div>
                                            <p className="text-sm text-gray-500">Rating</p>
                                            <div className="flex items-center space-x-1">
                                                <StarIcon className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                                                <span className="font-medium">{solution.rating}</span>
                                                <span className="text-sm text-gray-500">
                                                    ({solution.review_count || 0} reviews)
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {solution.problem_fit && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-900">Problem Fit</p>
                                        <p className="text-sm text-gray-600 mt-1">{solution.problem_fit}</p>
                                    </div>
                                )}

                                <div className="mt-6 space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Business Assessment *
                                        </label>
                                        {loadingAssessments ? (
                                            <p className="text-sm text-gray-500">Loading assessments...</p>
                                        ) : assessments.length === 0 ? (
                                            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                                                No business assessments found. Create an assessment under Businesses first.
                                            </p>
                                        ) : (
                                            <select
                                                value={selectedAssessmentId}
                                                onChange={(e) => setSelectedAssessmentId(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-sm bg-white"
                                            >
                                                {assessments.map((assessment) => (
                                                    <option key={assessment.id} value={assessment.id}>
                                                        {assessment.business?.name || 'Business'} — {assessment.title || `Assessment #${assessment.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleMatchSolution}
                                        disabled={matching || loadingAssessments || !selectedAssessmentId}
                                        className="px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    >
                                        {matching ? 'Matching...' : 'Match to Business Project →'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'training' && (
                            <div>
                                {solution.trainings && solution.trainings.length > 0 ? (
                                    <div className="space-y-3">
                                        {solution.trainings.map((training) => (
                                            <div key={training.id} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <h4 className="font-medium text-gray-900">
                                                            {training.title}
                                                        </h4>
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            {training.description}
                                                        </p>
                                                        <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
                                                            <span>⏱️ {training.duration_hours || 1} hours</span>
                                                            <span>•</span>
                                                            <span className="capitalize">📚 {training.training_type}</span>
                                                            {training.is_mandatory && (
                                                                <span className="text-red-500 font-medium">• Required</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            axios.post(`/solutions/training/${training.id}/start`)
                                                                .then(() => toast.success('Training started!'))
                                                                .catch((err) => toast.error(err.response?.data?.message || 'Failed to start training'));
                                                        }}
                                                        className="px-3 py-1 text-sm bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                                                    >
                                                        Start
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No training materials available</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'reviews' && (
                            <div>
                                {solution.reviews && solution.reviews.length > 0 ? (
                                    <div className="space-y-3">
                                        {solution.reviews.map((review) => (
                                            <div key={review.id} className="border-b border-gray-100 pb-3">
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex">
                                                        {[...Array(5)].map((_, i) => (
                                                            <StarIcon 
                                                                key={i} 
                                                                className={`w-4 h-4 ${
                                                                    i < review.rating 
                                                                        ? 'text-yellow-400 fill-yellow-400' 
                                                                        : 'text-gray-300'
                                                                }`} 
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-sm font-medium">
                                                        {review.title}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-700 mt-1">{review.review}</p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    By Intern • {new Date(review.created_at).toLocaleDateString()}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No reviews yet</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'demos' && (
                            <div>
                                {solution.demos && solution.demos.length > 0 ? (
                                    <div className="space-y-3">
                                        {solution.demos.map((demo) => (
                                            <div key={demo.id} className="border border-gray-200 rounded-lg p-4">
                                                <h4 className="font-medium text-gray-900">{demo.name}</h4>
                                                <p className="text-sm text-gray-600 mt-1">{demo.description}</p>
                                                <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500">
                                                    <span>⏱️ {demo.duration_minutes || 15} minutes</span>
                                                    {demo.presenter && (
                                                        <span>👤 {demo.presenter}</span>
                                                    )}
                                                </div>
                                                {demo.demo_url && (
                                                    <a 
                                                        href={demo.demo_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="mt-2 inline-block text-sm text-blue-600 hover:underline"
                                                    >
                                                        Watch Demo →
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500">No demos available</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Solutions;
